import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FlightProvider, useFlight } from './contexts/FlightContext';
import LoginScreen from './screens/LoginScreen';
import FlightSelectionScreen from './screens/FlightSelectionScreen';
import MainLayout from './components/MainLayout';
import { Tab, NotificationStation } from './types';
import { database } from './services/firebase';
import { ref, onValue } from 'firebase/database';

const getStationIcon = (station: NotificationStation) => {
    const iconTextMap: { [key in NotificationStation]?: string } = {
        'Crew Arrived': '[Crew]',
        '1st Pax IN': '[Pax]',
        'Last Pax': '[Pax]',
        '1st X-ray': '[Bag]',
        'Immigration In': '[ID]',
        'Immigration Out': '[ID]',
        'Duty Free': '[Cart]',
        'At Gate': '[Gate]',
        'At Aircraft': '[Plane]',
        'Ready For Boarding': '[Ticket]',
        'Doors Closed': '[Door]',
        'Push Back': '[Truck]',
        'Acceptance Finalized': '[OK]',
        'System Clear': '[Sys]',
        'Gate Clear': '[Gate]',
    };
    const text = iconTextMap[station] || '[!]';
    return <span className="w-8 h-8 mr-4 flex-shrink-0 flex items-center justify-center font-bold text-lg">{text}</span>;
};


const App: React.FC = () => {
  return (
    <AuthProvider>
      <FlightProvider>
        <AppContent />
      </FlightProvider>
    </AuthProvider>
  );
};

const LoadingIndicator: React.FC = () => (
    <div className="min-h-screen flex flex-col items-center justify-center text-center bg-slate-900">
        <h2 className="mt-6 text-2xl font-bold text-slate-200 animate-slide-up-fade-in">Preparing Dashboard...</h2>
        <p className="mt-2 text-slate-400 animate-slide-up-fade-in" style={{ animationDelay: '150ms' }}>Syncing real-time flight data.</p>
    </div>
);

const AppContent: React.FC = () => {
  const { user } = useAuth();
  const { selectedFlightData, notification: localNotification, areNotificationsEnabled, isLoading: isFlightDataLoading } = useFlight();
  const [showTour, setShowTour] = useState(false);
  const [isTourInitialized, setIsTourInitialized] = useState(false);
  const [isOnline, setIsOnline] = useState<boolean>(true);
  
  // State for global notifications from other users
  const [globalNotification, setGlobalNotification] = useState<{ station: NotificationStation; triggeredBy: string } | null>(null);
  const globalNotificationTimer = useRef<number | null>(null);
  const lastGlobalNotificationTimestamp = useRef<number | null>(null);

  useEffect(() => {
    const connectedRef = ref(database, '.info/connected');
    const unsubscribe = onValue(connectedRef, (snapshot) => {
        setIsOnline(snapshot.val() === true);
    });

    return () => {
        unsubscribe();
        if (globalNotificationTimer.current) {
            clearTimeout(globalNotificationTimer.current);
        }
    };
  }, []);

  // Effect for automatic app updates via service worker
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    let refreshing = false;
    const onControllerChange = () => {
      if (refreshing) return;
      window.location.reload();
      refreshing = true;
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    };
  }, []);

  // Effect to handle incoming remote notifications for visual toasts
  useEffect(() => {
    if (!selectedFlightData) return;
    const remoteNotification = selectedFlightData.lastNotification;
    if (!remoteNotification) return;

    // --- Visual Toast Logic for other users ---
    // Show a visual toast only if the notification is new and from another user
    if (remoteNotification.timestamp !== lastGlobalNotificationTimestamp.current) {
        lastGlobalNotificationTimestamp.current = remoteNotification.timestamp;
        
        if (remoteNotification.triggeredBy !== user?.username) {
            setGlobalNotification({
                station: remoteNotification.station,
                triggeredBy: remoteNotification.triggeredBy,
            });

            if (globalNotificationTimer.current) {
                clearTimeout(globalNotificationTimer.current);
            }
            globalNotificationTimer.current = window.setTimeout(() => {
                setGlobalNotification(null);
            }, 7000); // Show for 7 seconds
        }
    }
  }, [selectedFlightData, user?.username]);


  useEffect(() => {
    if (user && !isTourInitialized) {
      const hasCompletedTour = localStorage.getItem('hasCompletedTour');
      if (!hasCompletedTour) {
        setShowTour(true);
      }
      setIsTourInitialized(true);
    }
  }, [user, isTourInitialized]);
  

  const handleTourComplete = () => {
    localStorage.setItem('hasCompletedTour', 'true');
    setShowTour(false);
  };
  
  const handleDismissGlobalNotification = () => {
      if (globalNotificationTimer.current) {
          clearTimeout(globalNotificationTimer.current);
      }
      setGlobalNotification(null);
  };

  if (!user) {
    return <LoginScreen />;
  }

  if (isFlightDataLoading) {
      return <LoadingIndicator />;
  }
  
  const isFlightSessionActive = Object.keys(useFlight().activeFlights).length > 0;

  if (!isFlightSessionActive) {
    return <FlightSelectionScreen />;
  }

  const roleDefaultTabs: { [key: string]: Tab } = {
    ADMIN: Tab.Admin,
    SUPERVISOR: Tab.CounterDistribution,
    AGENT: Tab.CrewMovement,
    VIEWER: Tab.Overview,
  };
  
  const initialTab = roleDefaultTabs[user.role] || Tab.Overview;

  return (
    <>
      <div
        role="status"
        aria-live="polite"
        className={`fixed top-0 left-0 right-0 bg-amber-600 text-white text-center text-sm py-1.5 z-[60] shadow-lg transition-transform duration-300 ease-in-out ${isOnline ? '-translate-y-full' : 'translate-y-0'}`}
      >
        <div className="container mx-auto flex items-center justify-center gap-2">
          <span>You are offline. Changes will sync when reconnected.</span>
        </div>
      </div>
      <MainLayout initialTab={initialTab} showTour={showTour} onTourComplete={handleTourComplete} />
      {/* Local notifications (for user's own actions) */}
      {localNotification && (
        <div role="alert" className={`toast-notification fixed top-5 right-5 z-50 p-4 rounded-lg shadow-lg text-white ${localNotification.type === 'success' ? 'bg-gradient-to-r from-green-500 to-emerald-600 border border-green-400' : 'bg-gradient-to-r from-red-500 to-rose-600 border border-red-400'}`}>
          <div className="flex items-center">
            {localNotification.message}
          </div>
        </div>
      )}
      {/* Global notifications (from other users) */}
      {globalNotification && areNotificationsEnabled && (
        <div role="alert" className="toast-notification fixed top-5 left-1/2 -translate-x-1/2 z-50 p-4 rounded-lg shadow-2xl text-white bg-gradient-to-r from-sky-600 to-cyan-600 border border-sky-400 max-w-md w-full">
           <div className="flex items-center">
            {getStationIcon(globalNotification.station)}
            <div className="flex-grow">
                <p className="font-bold text-lg">{globalNotification.station} recorded</p>
                <p className="text-sm text-sky-200">by {globalNotification.triggeredBy}</p>
            </div>
            <button onClick={handleDismissGlobalNotification} className="ml-4 p-1 rounded-full text-sky-200 hover:bg-sky-700/50 transition-colors flex-shrink-0" aria-label="Dismiss notification">
                <span className="w-5 h-5">X</span>
            </button>
           </div>
        </div>
      )}
    </>
  );
};

export default App;